import { useEffect, useState } from "react";
import axios from "axios";
import { X, Plus, Minus, Flame, Loader2 } from "lucide-react";
import { API, BACKEND_URL, formatARS } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

export default function CustomizationModal({ product, onClose }) {
  const { addItem } = useCart();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(null);
  const [masa, setMasa] = useState("tradicional");
  const [extras, setExtras] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (!product) return;
    axios.get(`${API}/customization/${product.id}`)
      .then((r) => {
        setCfg(r.data);
        if (r.data.sizes?.length) setSize(r.data.sizes[0].key);
      })
      .finally(() => setLoading(false));
  }, [product]);

  if (!product) return null;

  const basePrice = (() => {
    if (!cfg?.hasCustomization) return product.precio || 0;
    const s = cfg.sizes.find((x) => x.key === size);
    return s?.precio ?? product.precio ?? 0;
  })();
  const masaExtra = cfg?.masas?.find((m) => m.key === masa)?.extra || 0;
  const extrasTotal = (cfg?.extras || []).filter((e) => extras.includes(e.key)).reduce((s, e) => s + e.extra, 0);
  const unit = basePrice + masaExtra + extrasTotal;
  const total = unit * qty;

  const toggleExtra = (k) => setExtras((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const confirm = () => {
    const personalizacion = cfg?.hasCustomization ? {
      tamano: size,
      masa,
      extras: [...extras],
    } : null;

    const extrasDetalle = cfg?.hasCustomization
      ? (cfg.extras || []).filter((e) => extras.includes(e.key)).map((e) => ({ label: e.label, extra: e.extra }))
      : [];
    const masaSel = cfg?.masas?.find((m) => m.key === masa);
    if (masaSel?.extra > 0) extrasDetalle.push({ label: `Masa ${masaSel.label}`, extra: masaSel.extra });

    const nombreCompleto = product.nombre + (cfg?.hasCustomization && size ? ` (${size === "grande" ? "Grande" : "Chica"})` : "");

    for (let i = 0; i < qty; i++) {
      addItem({
        ...product,
        precio: unit,
        precioGrande: null,
        precioChica: null,
      }, cfg?.hasCustomization ? size : null, { key: `${product.id}::${size || "u"}::${masa}::${extras.sort().join("-")}`, nombre: nombreCompleto, personalizacion, precioUnitario: unit, precioBase: basePrice, extrasDetalle });
    }
    toast.success(`${qty}× ${nombreCompleto} agregado`, { description: formatARS(total) });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <div
        data-testid="customization-modal"
        className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto thin-scroll bg-[#0F0F11] border border-line sm:rounded-xl rounded-t-2xl slide-up"
      >
        <div className="relative aspect-[16/9] bg-black overflow-hidden">
          {product.imagen && <img src={resolveImg(product.imagen)} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F11] via-transparent to-transparent" />
          <button data-testid="customization-close" onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 sm:p-6">
          <h3 className="font-heading text-3xl uppercase text-white">{product.nombre}</h3>
          {product.descripcion && <p className="text-sm text-zinc-400 mt-1">{product.descripcion}</p>}

          {loading ? (
            <div className="py-6 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando opciones...</div>
          ) : (
            <>
              {cfg?.hasCustomization && cfg.sizes.length > 0 && (
                <Section title="Tamaño">
                  <div className="grid grid-cols-2 gap-2">
                    {cfg.sizes.map((s) => (
                      <Chip key={s.key} testid={`custom-size-${s.key}`} active={size === s.key} onClick={() => setSize(s.key)}>
                        <div className="font-bold uppercase">{s.label}</div>
                        <div className="text-[11px] opacity-80">{formatARS(s.precio)}</div>
                      </Chip>
                    ))}
                  </div>
                </Section>
              )}

              {cfg?.hasCustomization && cfg.masas.length > 0 && (
                <Section title="Tipo de masa">
                  <div className="grid grid-cols-2 gap-2">
                    {cfg.masas.map((m) => (
                      <Chip key={m.key} testid={`custom-masa-${m.key}`} active={masa === m.key} onClick={() => setMasa(m.key)}>
                        <div className="font-bold uppercase">{m.label}</div>
                        {m.extra > 0 && <div className="text-[11px] opacity-80">+{formatARS(m.extra)}</div>}
                      </Chip>
                    ))}
                  </div>
                </Section>
              )}

              {cfg?.hasCustomization && cfg.extras.length > 0 && (
                <Section title="Ingredientes extra">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {cfg.extras.map((e) => {
                      const active = extras.includes(e.key);
                      return (
                        <button
                          key={e.key}
                          data-testid={`custom-extra-${e.key}`}
                          onClick={() => toggleExtra(e.key)}
                          className={`text-left flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${
                            active ? "border-gold bg-gold/10" : "border-line bg-panel hover:border-white/30"
                          }`}
                        >
                          <span className="text-sm text-white font-medium">{e.label}</span>
                          <span className="text-xs text-gold font-bold">+{formatARS(e.extra)}</span>
                        </button>
                      );
                    })}
                  </div>
                </Section>
              )}

              <Section title="Cantidad">
                <div className="inline-flex items-center gap-2 bg-black border border-line rounded-full p-1">
                  <button data-testid="custom-qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 rounded-full text-white flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                  <span data-testid="custom-qty-value" className="font-heading text-2xl text-white w-8 text-center">{qty}</span>
                  <button data-testid="custom-qty-inc" onClick={() => setQty((q) => q + 1)} className="w-9 h-9 rounded-full text-white flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                </div>
              </Section>
            </>
          )}

          <button
            data-testid="customization-confirm"
            onClick={confirm}
            disabled={loading}
            className="mt-6 w-full flex items-center justify-between gap-3 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase tracking-widest px-4 py-4 rounded-lg transition-colors"
          >
            <span className="flex items-center gap-2"><Flame className="w-4 h-4" strokeWidth={3} /> Agregar al carrito</span>
            <span className="font-heading text-2xl">{formatARS(total)}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <div className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-2">{title}</div>
      {children}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-2.5 rounded-lg border text-white text-sm transition-colors ${
        active ? "border-brand bg-brand/10" : "border-line bg-panel hover:border-white/30"
      }`}
    >
      {children}
    </button>
  );
}
